import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { fade } from '@material-ui/core';
import { makeStyles, withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { capitalize } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/utils';

const MUI_COLORS = [
  'primary',
  'secondary',
];

export const COLORS = [
  ...MUI_COLORS,
  'accentGreen',
  'accentBlue',
  'accentBlue2',
  'darkGray',
  'lightGray',
  'mediumGray',
  'primaryGray',
  'red',
  'orange',
  'seafoam',
  'success',
  'purple',
  'none', // If you would not like to have either a stroke or a fill
];

const BOX_SIZES = {
  xxs: 2,
  xs: 5,
  sm: 8,
  md: 10,
  lg: 13,
  xl: 17,
};

const styles = () => ({
  root: {},
  rootBackground: {
    borderRadius: '50%',
  },
});

const getFillColor = (color) => {
  return {
    fill: color,
  };
};

const getStrokeColor = (color) => {
  return {
    stroke: color,
  };
};

const getBackgroundColor = (color) => {
  return {
    backgroundColor: fade(color, 0.1),
  };
};

const useCustomStyles = makeStyles(theme => ({
  rootFillPrimary: getFillColor(theme.palette.primary.main),
  rootFillSecondary: getFillColor(theme.palette.secondary.main),
  rootFillAccentBlue: getFillColor(theme.palette.facet.accentBlue),
  rootFillAccentBlue2: getFillColor(theme.palette.facet.accentBlue2),
  rootFillAccentGreen: getFillColor(theme.palette.facet.accentGreen),
  rootFillDarkGray: getFillColor(theme.palette.facet.darkGray),
  rootFillLightGray: getFillColor(theme.palette.facet.lightGray),
  rootFillMediumGray: getFillColor(theme.palette.facet.mediumGray),
  rootFillPrimaryGray: getFillColor(theme.palette.facet.primaryGray),
  rootFillRed: getFillColor(theme.palette.facet.red),
  rootFillOrange: getFillColor(theme.palette.facet.orange),
  rootFillSeafoam: getFillColor(theme.palette.facet.seafoam),
  rootFillSuccess: getFillColor(theme.palette.success.main),
  rootFillPurple: getFillColor(theme.palette.facet.purple),
  rootFillNone: { fill: 'none' },
  rootStrokePrimary: getStrokeColor(theme.palette.primary.main),
  rootStrokeSecondary: getStrokeColor(theme.palette.secondary.main),
  rootStrokeAccentBlue: getStrokeColor(theme.palette.facet.accentBlue),
  rootStrokeAccentBlue2: getStrokeColor(theme.palette.facet.accentBlue2),
  rootStrokeAccentGreen: getFillColor(theme.palette.facet.accentGreen),
  rootStrokeDarkGray: getStrokeColor(theme.palette.facet.darkGray),
  rootStrokeLightGray: getStrokeColor(theme.palette.facet.lightGray),
  rootStrokeMediumGray: getStrokeColor(theme.palette.facet.mediumGray),
  rootStrokePrimaryGray: getStrokeColor(theme.palette.facet.primaryGray),
  rootStrokeRed: getStrokeColor(theme.palette.facet.red),
  rootStrokeOrange: getStrokeColor(theme.palette.facet.orange),
  rootStrokeSeafoam: getStrokeColor(theme.palette.facet.seafoam),
  rootStrokeSuccess: getStrokeColor(theme.palette.success.main),
  rootStrokePurple: getStrokeColor(theme.palette.facet.purple),
  rootStrokeNone: { stroke: 'none' },
  rootBackgroundColorPrimary: getBackgroundColor(theme.palette.primary.main),
  rootBackgroundColorSecondary: getBackgroundColor(theme.palette.secondary.main),
  rootBackgroundColorAccentBlue: getBackgroundColor(theme.palette.facet.accentBlue),
  rootBackgroundColorAccentBlue2: getBackgroundColor(theme.palette.facet.accentBlue2),
  rootBackgroundColorAccentGreen: getBackgroundColor(theme.palette.facet.accentGreen),
  rootBackgroundColorDarkGray: getBackgroundColor(theme.palette.facet.darkGray),
  rootBackgroundColorLightGray: getBackgroundColor(theme.palette.facet.lightGray),
  rootBackgroundColorMediumGray: getBackgroundColor(theme.palette.facet.mediumGray),
  rootBackgroundColorPrimaryGray: getBackgroundColor(theme.palette.facet.primaryGray),
  rootBackgroundColorRed: getBackgroundColor(theme.palette.facet.red),
  rootBackgroundColorOrange: getBackgroundColor(theme.palette.facet.orange),
  rootBackgroundColorSeafoam: getBackgroundColor(theme.palette.facet.seafoam),
  rootBackgroundColorSuccess: getBackgroundColor(theme.palette.success.main),
  rootBackgroundColorPurple: getBackgroundColor(theme.palette.facet.purple),
  rootBackgroundColorNone: {},
  rootBoxSizeXxs: {
    height: theme.spacing(BOX_SIZES.xxs),
    width: theme.spacing(BOX_SIZES.xxs),
  },
  rootBoxSizeXs: {
    height: theme.spacing(BOX_SIZES.xs),
    width: theme.spacing(BOX_SIZES.xs),
  },
  rootBoxSizeSm: {
    height: theme.spacing(BOX_SIZES.sm),
    width: theme.spacing(BOX_SIZES.sm),
  },
  rootBoxSizeMd: {
    height: theme.spacing(BOX_SIZES.md),
    width: theme.spacing(BOX_SIZES.md),
  },
  rootBoxSizeLg: {
    height: theme.spacing(BOX_SIZES.lg),
    width: theme.spacing(BOX_SIZES.lg),
  },
  rootBoxSizeXl: {
    height: theme.spacing(BOX_SIZES.xl),
    width: theme.spacing(BOX_SIZES.xl),
  },
}));

const Icon = React.forwardRef((props, ref) => {
  const {
    boxSize,
    className,
    classes,
    fill,
    stroke,
    icon: IconComponent,
    showBackground,
    ...rest
  } = props;
  const customClasses = useCustomStyles();
  const {
    root: rootClass,
    rootBackground: rootBackgroundClass,
    ...restClasses
  } = classes;

  return (
    <IconComponent
      ref={ref}
      classes={{
        ...restClasses,
        root: classNames(
          classes.root,
          className, {
            [customClasses[`rootStroke${capitalize(stroke)}`]]: true,
            [customClasses[`rootFill${capitalize(fill)}`]]: true,
            [rootBackgroundClass]: showBackground,
            [customClasses[`rootBackgroundColor${capitalize(fill)}`]]: showBackground,
            [customClasses[`rootBoxSize${capitalize(String(boxSize))}`]]: Boolean(BOX_SIZES[boxSize]),
          },
        ),
      }}
      {...rest}
    />
  );
});

Icon.defaultProps = {
  boxSize: undefined,
  className: '',
  fill: 'primary',
  stroke: 'none',
  showBackground: false,
};

Icon.propTypes = {
  boxSize: PropTypes.oneOf(Object.keys(BOX_SIZES)),
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  showBackground: PropTypes.bool,
  fill: PropTypes.oneOf(COLORS),
  stroke: PropTypes.oneOf(COLORS),
  icon: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
};

export default withStyles(styles)(Icon);
